import { ReactNode, useEffect, useState, useRef, useMemo } from "react";
import { useNostr } from "@nostrify/react";
import { useQuery } from "@tanstack/react-query";
import type { NostrEvent } from "@nostrify/nostrify";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { GMContext, type GMContextType } from "@/contexts/GMContext";
import { containsGM } from "@/lib/gmDetection";
import { isDirectReplyTo } from "@/lib/nip10";

interface GMProviderProps {
  children: ReactNode;
}

export function GMProvider({ children }: GMProviderProps) {
  const { user } = useCurrentUser();
  const { nostr } = useNostr();

  const [gmNotes, setGmNotes] = useState<NostrEvent[]>([]);
  const [userReplies, setUserReplies] = useState<NostrEvent[]>([]);
  const [isScanning, setIsScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState({ scanned: 0, found: 0 });

  // Use refs to track events across subscription restarts and prevent duplicates
  const gmNotesRef = useRef<Map<string, NostrEvent>>(new Map());
  const userRepliesRef = useRef<Map<string, NostrEvent>>(new Map());
  // Track if we've done an initial load for this user
  const initialLoadDoneRef = useRef<string | null>(null);

  // Fetch user's contacts (kind 3)
  const { data: contacts = [], isLoading: isLoadingContacts } = useQuery({
    queryKey: ["contacts", user?.pubkey],
    queryFn: async (c) => {
      if (!user) return [];
      const events = await nostr.query(
        [{ kinds: [3], authors: [user.pubkey], limit: 1 }],
        { signal: c.signal },
      );
      if (events.length === 0) return [];

      // Extract pubkeys from p tags
      return events[0].tags
        .filter(([tag]) => tag === "p")
        .map(([, pubkey]) => pubkey);
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Live subscription for GM notes from contacts - stays open for new notes
  useEffect(() => {
    if (!user || contacts.length === 0) return;

    // Skip if we already loaded for this user (prevents re-fetching on navigation)
    if (
      initialLoadDoneRef.current === user.pubkey &&
      gmNotesRef.current.size > 0
    ) {
      console.log("[GM Protocol] Using cached GM notes, skipping re-fetch");
      return;
    }

    const startOfToday = Math.floor(new Date().setHours(0, 0, 0, 0) / 1000);
    const controller = new AbortController();

    // Only reset if this is a different user
    if (initialLoadDoneRef.current !== user.pubkey) {
      gmNotesRef.current.clear();
      setGmNotes([]);
      setScanProgress({ scanned: 0, found: 0 });
    }

    setIsScanning(true);

    console.log(
      `[GM Protocol] 🚀 Starting live GM subscription for ${contacts.length} contacts`,
    );

    // Some relays have limits on authors array size (typically 500-1000)
    const BATCH_SIZE = 500;
    const contactBatches: string[][] = [];
    for (let i = 0; i < contacts.length; i += BATCH_SIZE) {
      contactBatches.push(contacts.slice(i, i + BATCH_SIZE));
    }

    let totalScanned = 0;
    let totalFound = 0;
    let eoseCount = 0;

    // Run subscriptions - these stay open after EOSE for live updates
    const runSubscriptions = async () => {
      const promises = contactBatches.map(async (batch, batchIndex) => {
        const filter = {
          kinds: [1],
          authors: batch,
          since: startOfToday,
        };

        try {
          for await (const msg of nostr.req([filter], {
            signal: controller.signal,
          })) {
            if (msg[0] === "EVENT") {
              const event = msg[2] as NostrEvent;
              totalScanned++;

              // Update progress periodically
              if (totalScanned % 25 === 0) {
                setScanProgress({ scanned: totalScanned, found: totalFound });
              }

              if (containsGM(event.content)) {
                totalFound++;
                if (!gmNotesRef.current.has(event.id)) {
                  gmNotesRef.current.set(event.id, event);
                  setGmNotes(Array.from(gmNotesRef.current.values()));
                }
              }
            } else if (msg[0] === "EOSE") {
              eoseCount++;
              console.log(
                `[GM Protocol] EOSE for batch ${batchIndex + 1}/${contactBatches.length}`,
              );

              // Mark initial scan complete when all batches have sent EOSE
              if (eoseCount >= contactBatches.length) {
                setIsScanning(false);
                setScanProgress({ scanned: totalScanned, found: totalFound });
                initialLoadDoneRef.current = user.pubkey;
                console.log(
                  `[GM Protocol] ✅ Initial scan complete: ${totalScanned} scanned, ${totalFound} GM notes found`,
                );
              }
              // Don't break - keep subscription open for live updates
            }
          }
        } catch (error) {
          if ((error as Error).name === "AbortError") {
            console.log(
              `[GM Protocol] Subscription closed for batch ${batchIndex + 1}`,
            );
          } else {
            console.error(`[GM Protocol] ❌ Error in subscription:`, error);
          }
        }
      });

      await Promise.all(promises);
    };

    runSubscriptions();

    return () => {
      controller.abort();
    };
  }, [nostr, contacts, user]);

  // Live subscription for user's own replies
  useEffect(() => {
    if (!user) return;

    // Skip if we already have data for this user
    if (
      initialLoadDoneRef.current === user.pubkey &&
      userRepliesRef.current.size > 0
    ) {
      return;
    }

    const startOfToday = Math.floor(new Date().setHours(0, 0, 0, 0) / 1000);
    const controller = new AbortController();

    // Only reset if this is a different user
    if (initialLoadDoneRef.current !== user.pubkey) {
      userRepliesRef.current.clear();
      setUserReplies([]);
    }

    const runSubscription = async () => {
      try {
        for await (const msg of nostr.req(
          [
            {
              kinds: [1],
              authors: [user.pubkey],
              since: startOfToday,
            },
          ],
          { signal: controller.signal },
        )) {
          if (msg[0] === "EVENT") {
            const event = msg[2] as NostrEvent;
            if (
              containsGM(event.content) &&
              event.tags.some(([tag]) => tag === "e")
            ) {
              if (!userRepliesRef.current.has(event.id)) {
                userRepliesRef.current.set(event.id, event);
                setUserReplies(Array.from(userRepliesRef.current.values()));
              }
            }
          }
          // Don't break on EOSE - keep subscription open for live updates
        }
      } catch (error) {
        if ((error as Error).name !== "AbortError") {
          console.error(
            "[GM Protocol] Error in user replies subscription:",
            error,
          );
        }
      }
    };

    runSubscription();

    return () => {
      controller.abort();
    };
  }, [nostr, user]);

  // Live subscription for the user's own root GM note today
  const [myGMNote, setMyGMNoteState] = useState<NostrEvent | null>(null);
  const [isLoadingMyGMNote, setIsLoadingMyGMNote] = useState(false);
  const myGMNoteUserRef = useRef<string | null>(null);

  const setMyGMNote = (note: NostrEvent) => {
    myGMNoteUserRef.current = note.pubkey;
    setMyGMNoteState((current) =>
      current && current.created_at <= note.created_at ? current : note,
    );
  };

  useEffect(() => {
    if (!user) return;

    // Skip if we already found today's note for this user
    if (myGMNoteUserRef.current === user.pubkey && myGMNote) return;

    const startOfToday = Math.floor(new Date().setHours(0, 0, 0, 0) / 1000);
    const controller = new AbortController();

    if (myGMNoteUserRef.current !== user.pubkey) {
      setMyGMNoteState(null);
    }

    setIsLoadingMyGMNote(true);

    const runSubscription = async () => {
      try {
        for await (const msg of nostr.req(
          [
            {
              kinds: [1],
              authors: [user.pubkey],
              since: startOfToday,
            },
          ],
          { signal: controller.signal },
        )) {
          if (msg[0] === "EVENT") {
            const event = msg[2] as NostrEvent;
            if (
              containsGM(event.content) &&
              !event.tags.some(([tag]) => tag === "e")
            ) {
              setMyGMNote(event);
            }
          } else if (msg[0] === "EOSE") {
            setIsLoadingMyGMNote(false);
          }
          // Don't break on EOSE - keep subscription open for live updates
        }
      } catch (error) {
        if ((error as Error).name !== "AbortError") {
          console.error(
            "[GM Protocol] Error checking for today's GM note:",
            error,
          );
        }
      }
    };

    runSubscription();

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nostr, user]);

  // Live subscription for direct replies to the user's own GM note
  const [myGMReplies, setMyGMReplies] = useState<NostrEvent[]>([]);
  const myGMRepliesRef = useRef<Map<string, NostrEvent>>(new Map());
  const myGMRepliesNoteIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!myGMNote) return;

    const controller = new AbortController();

    // Only reset if this is a different note than the one we last subscribed to
    if (myGMRepliesNoteIdRef.current !== myGMNote.id) {
      myGMRepliesNoteIdRef.current = myGMNote.id;
      myGMRepliesRef.current.clear();
      setMyGMReplies([]);
    }

    const runSubscription = async () => {
      try {
        for await (const msg of nostr.req(
          [{ kinds: [1], "#e": [myGMNote.id] }],
          { signal: controller.signal },
        )) {
          if (msg[0] === "EVENT") {
            const event = msg[2] as NostrEvent;
            if (
              event.pubkey !== myGMNote.pubkey &&
              isDirectReplyTo(event, myGMNote.id)
            ) {
              if (!myGMRepliesRef.current.has(event.id)) {
                myGMRepliesRef.current.set(event.id, event);
                setMyGMReplies(
                  Array.from(myGMRepliesRef.current.values()).sort(
                    (a, b) => a.created_at - b.created_at,
                  ),
                );
              }
            }
          }
          // Don't break on EOSE - keep subscription open for live updates
        }
      } catch (error) {
        if ((error as Error).name !== "AbortError") {
          console.error(
            "[GM Protocol] Error in my GM replies subscription:",
            error,
          );
        }
      }
    };

    runSubscription();

    return () => {
      controller.abort();
    };
  }, [nostr, myGMNote]);

  // Clear data when user logs out
  useEffect(() => {
    if (!user) {
      gmNotesRef.current.clear();
      userRepliesRef.current.clear();
      myGMRepliesRef.current.clear();
      initialLoadDoneRef.current = null;
      myGMNoteUserRef.current = null;
      setGmNotes([]);
      setUserReplies([]);
      setMyGMNoteState(null);
      setMyGMReplies([]);
      setIsScanning(false);
      setIsLoadingMyGMNote(false);
      setScanProgress({ scanned: 0, found: 0 });
    }
  }, [user]);

  const contextValue: GMContextType = useMemo(
    () => ({
      gmNotes,
      userReplies,
      isScanning,
      scanProgress,
      contactsLoaded: !isLoadingContacts,
      contactCount: contacts.length,
      myGMNote,
      isLoadingMyGMNote,
      myGMReplies,
      setMyGMNote,
    }),
    [
      gmNotes,
      userReplies,
      isScanning,
      scanProgress,
      isLoadingContacts,
      contacts.length,
      myGMNote,
      isLoadingMyGMNote,
      myGMReplies,
    ],
  );

  return (
    <GMContext.Provider value={contextValue}>{children}</GMContext.Provider>
  );
}
